import { kGcsUserFileUploadsBucketRef, kHiddenFileForFolderName } from '@/constants/constants-gcs'
import { FullMetadata, getMetadata, listAll, ref, StorageReference } from 'firebase/storage'
import { getAppStorage } from '@/firebase/storage-routing'
import { useEffect, useState, useCallback, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useAppSelector } from '@/store/store-hooks'
import { selectRenderedMatterNameMap } from '@/matter/store/selectors'
import pLimit from 'p-limit'
import { determineGcsFileStatus, FileProcessingStage } from '@/routes/dashboard/files/utils/gcs-file-metadata-logic'
import { GCSFileMetadataStatusIndicator } from '@/routes/dashboard/files/utils/gcs-file-self-assessment'

export type FileNode = {
  id: string
  name: string
  isFolder: boolean
  isLoading: boolean
  isExpanded?: boolean
  parentId: string | null
  metadata?: FullMetadata
  displayName: string
  isVirtualFolder?: boolean
}

type UseProcessedFilesProps = {
  path: string
  fileReadyAtStage?: FileProcessingStage
}

/**
 * `useProcessedFiles` is a custom hook that fetches and manages a file structure from Google Cloud Storage.
 * It uses a flat data structure for performance and easier state management, especially for real-time updates.
 *
 * @param path - The GCS path to fetch files from
 * @param fileReadyAtStage - What stage of processing the file must reach to be ready to use (see FileProcessingStage enum).
 *                           Defaults to EMBEDDINGS_INDEXED which waits for the entire pipeline including Pinecone indexing.
 *                           This parameter is passed through to `determineGcsFileStatus`.
 */
export function useProcessedFiles({ path, fileReadyAtStage }: UseProcessedFilesProps) {
  const [nodes, setNodes] = useState<Record<string, FileNode>>({})
  const [loading, setLoading] = useState<boolean>(true)
  const loadedFolders = useRef(new Set<string>())
  const renderedMatterNameMap = useAppSelector(selectRenderedMatterNameMap)
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })

  const getAndProcessFolderContents = useCallback(
    async (currentPath: string): Promise<Record<string, FileNode>> => {
      loadedFolders.current.add(currentPath)
      const listRef = ref(storage, currentPath)
      const response = await listAll(listRef)
      const newNodes: Record<string, FileNode> = {}

      // Process folders
      for (const folderRef of response.prefixes) {
        const displayName = renderedMatterNameMap.get(folderRef.name) || folderRef.name
        newNodes[folderRef.fullPath] = {
          id: folderRef.fullPath,
          name: folderRef.name,
          isFolder: true,
          isLoading: false,
          isExpanded: false,
          parentId: currentPath,
          displayName,
        }
      }

      // Process files
      const limit = pLimit(10)
      const metadataPromises = response.items.map((itemRef) => {
        if (itemRef.name === kHiddenFileForFolderName) {
          return Promise.resolve(null)
        }
        return limit(async () => {
          const metadata = await getMetadata(itemRef)
          return { itemRef, metadata }
        })
      })

      const metadataResults = (await Promise.all(metadataPromises)).filter(
        (result): result is { itemRef: StorageReference; metadata: FullMetadata } => result !== null
      )

      for (const { itemRef, metadata } of metadataResults) {
        newNodes[itemRef.fullPath] = {
          id: itemRef.fullPath,
          name: itemRef.name,
          isFolder: false,
          isLoading: false,
          metadata,
          parentId: currentPath,
          displayName: itemRef.name,
        }
      }
      return newNodes
    },
    [storage, renderedMatterNameMap]
  )

  // Effect to load the initial set of files and folders
  useEffect(() => {
    let isMounted = true
    async function loadInitialFiles() {
      setLoading(true)
      try {
        const newNodes = await getAndProcessFolderContents(path)
        if (isMounted) {
          setNodes(newNodes)
        }
      } catch (error) {
        Sentry.captureException(new Error('Failed to load initial files'), {
          extra: { path, error: JSON.stringify(error) },
        })
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadInitialFiles()

    return () => {
      isMounted = false
    }
  }, [path, getAndProcessFolderContents])

  // Effect for polling files that are still processing
  useEffect(() => {
    const pollMetadata = async (fileId: string) => {
      try {
        const fileRef = ref(storage, fileId)
        const metadata = await getMetadata(fileRef)
        const status = determineGcsFileStatus({ fileMetadata: metadata, fileReadyAtStage })

        if (status.status === GCSFileMetadataStatusIndicator.PROCESSING) {
          setTimeout(() => pollMetadata(fileId), 5000)
        } else {
          setNodes((prevNodes) => {
            const existingNode = prevNodes[fileId]
            if (!existingNode) return prevNodes
            return { ...prevNodes, [fileId]: { ...existingNode, metadata, isLoading: false } }
          })
        }
      } catch (error) {
        console.error('Error polling for metadata:', error)
        setNodes((prevNodes) => {
          const existingNode = prevNodes[fileId]
          if (!existingNode) return prevNodes
          return { ...prevNodes, [fileId]: { ...existingNode, isLoading: false } }
        })
      }
    }

    Object.values(nodes).forEach((node) => {
      if (!node.isFolder && node.metadata) {
        const status = determineGcsFileStatus({ fileMetadata: node.metadata, fileReadyAtStage })
        if (status.status === GCSFileMetadataStatusIndicator.PROCESSING) {
          pollMetadata(node.id)
        }
      }
    })
  }, [nodes, storage, fileReadyAtStage])

  // Function to expand a folder and load its children
  const expandFolder = useCallback(
    async (folderId: string): Promise<Record<string, FileNode> | null> => {
      const folderNode = nodes[folderId]
      // If the folder's children are already loaded (indicated by isExpanded), return them from state.
      if (folderNode && folderNode.isExpanded && !folderNode.isLoading) {
        const existingChildren = Object.values(nodes).filter((node) => node.parentId === folderId)
        if (existingChildren.length > 0) {
          return existingChildren.reduce((acc, child) => {
            acc[child.id] = child
            return acc
          }, {} as Record<string, FileNode>)
        }
      }

      setNodes((prevNodes) => {
        const existingNode = prevNodes[folderId]
        if (!existingNode) return prevNodes
        return { ...prevNodes, [folderId]: { ...existingNode, isLoading: true, isExpanded: true } }
      })

      try {
        const newNodes = await getAndProcessFolderContents(folderId)
        setNodes((prevNodes) => {
          const existingNode = prevNodes[folderId]
          if (!existingNode) return { ...prevNodes, ...newNodes }
          return { ...prevNodes, ...newNodes, [folderId]: { ...existingNode, isLoading: false } }
        })
        return newNodes
      } catch (error) {
        Sentry.captureException(new Error('Failed to expand folder'), {
          extra: { folderId, error: JSON.stringify(error) },
        })
        setNodes((prevNodes) => {
          const existingNode = prevNodes[folderId]
          if (!existingNode) return prevNodes
          return { ...prevNodes, [folderId]: { ...existingNode, isLoading: false } }
        })
        return null
      }
    },
    [getAndProcessFolderContents, nodes]
  )

  const refresh = useCallback(
    async (selectedFileIds: string[] = []): Promise<string[]> => {
      const foldersToExpand = new Set<string>()

      // 1. Identify all folders to expand from selectedFileIds
      for (const fileId of selectedFileIds) {
        const pathSegments = fileId.split('/')
        // Start at 3 for root level of full GCS path. Ex: users/user_id/top_level_folder
        for (let i = 3; i < pathSegments.length; i++) {
          const folderId = pathSegments.slice(0, i).join('/')
          foldersToExpand.add(folderId)
        }
      }

      try {
        // 2. Consolidate all folders that need to be refreshed
        const folderIdsToRefresh = new Set(loadedFolders.current)
        foldersToExpand.forEach((folderId) => folderIdsToRefresh.add(folderId))
        const folderIdsToRefreshArray = Array.from(folderIdsToRefresh)

        // 3. Refresh all necessary folders
        const refreshPromises = folderIdsToRefreshArray.map((folderId) => getAndProcessFolderContents(folderId))
        const results = await Promise.all(refreshPromises)

        // 4. Update state
        setNodes((prevNodes) => {
          const newNodes = { ...prevNodes }

          // Handle deletions: check old children against new results for each refreshed folder
          folderIdsToRefreshArray.forEach((folderId, index) => {
            const oldChildren = Object.values(prevNodes).filter((node) => node.parentId === folderId)
            const newChildren = results[index] // This is the map of new nodes for this folder
            const newChildrenIds = new Set(Object.keys(newChildren))
            oldChildren.forEach((child) => {
              if (!newChildrenIds.has(child.id)) {
                delete newNodes[child.id]
              }
            })
          })

          // Combine all new nodes from the results
          const freshNodes = results.reduce((acc, result) => ({ ...acc, ...result }), {})

          // Return the updated state
          return { ...newNodes, ...freshNodes }
        })
      } catch (error) {
        Sentry.captureException(new Error('Failed to refresh folders'), {
          extra: { error: JSON.stringify(error) },
        })
      }

      // 5. Return the list of folders to expand
      return Array.from(foldersToExpand)
    },
    [getAndProcessFolderContents]
  )

  return { loading, nodes, expandFolder, refresh }
}
